import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { I18n } from 'react-redux-i18n';
import get from "lodash.get";
import { Eye, EyeOff, DollarSign, Edit3, Send, RotateCcw, ExternalLink, History } from 'lucide-react';

import { getSaleRecord, getSaleRelatedRecord } from '../store/ticketingActions';
import { showReceiptConfirmation, showRefundConfirmation } from '../reducer';
import BasePageView from '../../../components/common/basePageView';
import { Record } from '../../../modules/bin/utility';
import { RouteParams } from '../../../types/routeParams';
import { RootState } from '../../../store';
import { useAppDispatch, usePageTitle } from '../../../hooks';
import { TicketingStatus, TicketingHeader } from '../components';
import { TicketPaymentTable } from './TicketPaymentTable';
import { TicketInformationTable } from './TicketInformationTable';
import { BillingInformationTable } from './BillingInformationTable';
import { TicketInstallmentTable } from './TicketInstallmentTable';
import { TicketCouponCodes } from './TicketCouponCodes';
import { CollectPaymentWindow } from '../modals/collectPaymentWindow';
import { TicketCollectPayment } from './TicketCollectPayment';
import { ViewPaymentUrlWindow } from '../modals/viewPaymentUrlWindow';
import { MerchandiseInformationTable } from './MerchandiseInformationTable';
import { DonationPageInformation } from '../../donation/view/donationPageInformation';
import { OriginCard } from '../../../components/common/OriginCard/OriginCard';
import ReceiptSaleWindowContainer from '../modals/receiptSaleWindow';
import RefundSaleWindow from '../modals/refundSaleWindow/RefundSaleWindow';
import {
  EntityHeaderActions
} from '../../../components/common/EntityHeaderActions/EntityHeaderActions';
import type { EntityActionsMenuItem } from '../../../components/common/EntityActionsDropdown/EntityActionsDropdown';
import config from '../../../constants';
import { isProduction } from '../../../utils/environmentUtils';
import { stripeName } from '../../organization/constants';
import { CONTEXT_MENU_KEYS } from '../../../constants/contextMenuEntries';

const ViewTicketing = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const ticketing = useSelector((state: RootState) => state.ticketing);
  const isSystemAdmin = useSelector((state: RootState) => state.session?.isSystemAdmin);
  const { saleId } = useParams<RouteParams>();
  const [showEmptyFields, setShowEmptyFields] = useState(false);
  const [displayOrgCurrency, setDisplayOrgCurrency] = useState(false);

  // Set page title
  const title = useMemo(() => {
    if (!saleId) return 'Sale | Grassrootz';
    return `Sale #${saleId} | Grassrootz`;
  }, [saleId]);
  usePageTitle(title);

  const isNotStarted = Record.notStarted(ticketing.record);
  const recordData = ticketing.record?.data?.data;
  const isInstallmentsEnabled = recordData?.financials?.installments?.enabled;
  const paymentCompleted =
    recordData?.financials?.installments?.paymentCompleted;
  const paymentCollectionStopped =
    recordData?.financials?.installments?.paymentCollectionStopped;
  const installmentManualPaymentRequests =
    ticketing?.relatedRecord?.data?.installmentManualPaymentRequests;

  useEffect(() => {
    if (!saleId) return; // Guard against undefined saleId
    
    if (!ticketing?.sale?.refundConfirmation) {
      dispatch(getSaleRecord(saleId));
      dispatch(getSaleRelatedRecord(saleId));
    } else if (ticketing?.sale?.collectPaymentWindow?.confirmation) {
      dispatch(getSaleRelatedRecord(saleId));
    }
  }, [
    saleId,
    ticketing?.sale?.refundConfirmation,
    ticketing?.sale?.collectPaymentWindow?.confirmation
  ]);

  const handleToggleEmptyFields = useCallback(() => {
    setShowEmptyFields(prev => !prev);
  }, []);

  const handleToggleCurrencyDisplay = useCallback(() => {
    setDisplayOrgCurrency(prev => !prev);
  }, []);

  const handleEditSale = useCallback(() => {
    if (!saleId) return;
    navigate(`/sales/edit/${saleId}/invoice-information`);
  }, [navigate, saleId]);

  const showCurrencyToggle = useMemo(() => {
    const currency = get(recordData, "currency");
    const orgCurrency = get(recordData, "organization.currency");
    const exchangeRate = get(recordData, "financials.exchangeRate");
    return currency !== orgCurrency && Boolean(exchangeRate);
  }, [recordData]);

  const emptyFieldsLabel = useMemo(
    () =>
      I18n.t(
        `ticketing.edit.buttons.${
          showEmptyFields ? 'hide' : 'show'
        }-empty-fields-link`
      ),
    [showEmptyFields]
  );

  const currencyToggleLabel = useMemo(() => {
    if (!showCurrencyToggle) return '';
    const orgCurrency = get(recordData, "organization.currency");
    const currency = get(recordData, "currency");
    return I18n.t('ticketing.edit.buttons.show-different-currency-link', {
      currency: showCurrencyToggle && displayOrgCurrency ? currency : orgCurrency
    });
  }, [showCurrencyToggle, displayOrgCurrency, recordData]);

  const editLabel = I18n.t('ticketing.edit.titles.view');
  const actionsTriggerLabel = I18n.t('campaign.performance.header.actions');

  // Action handlers for migrated TicketActions
  const handleReissueInvoice = useCallback(() => {
    dispatch(showReceiptConfirmation(recordData, 'sale'));
  }, [dispatch, recordData]);

  const handleRefundOrCancel = useCallback(() => {
    dispatch(showRefundConfirmation(recordData, 'sale'));
  }, [dispatch, recordData]);

  // Compute Stripe URL
  const stripeUrl = useMemo(() => {
    const financials = recordData?.financials;
    const chargeKey = get(financials, 'platformDetails.chargeKey');
    const intentKey = get(financials, 'platformDetails.intentKey');
    const installmentKey = get(financials, 'platformDetails.installmentKey');
    const paymentKey = intentKey || chargeKey;

    if (!paymentKey && !installmentKey) return null;

    // Get custom payment platform from related record
    const customPaymentPlatform = ticketing.relatedRecord?.data?.data?.paymentPlatform;
    const isRelatedLoaded = Record.isRecordReady(ticketing.relatedRecord);

    let accountKey: string | null = null;
    if (customPaymentPlatform) {
      accountKey = customPaymentPlatform.accountKey;
    }

    const sysadminAccountId = config.STRIPE_SYSADMIN_ACCOUNT_ID;
    const isProductionEnv = isProduction();
    const accountKeyContext = isProductionEnv ? accountKey : `${accountKey}/test`;
    const sysadminBaseUrl = sysadminAccountId
      ? `https://dashboard.stripe.com/${sysadminAccountId}/connect/view-as/${accountKeyContext}`
      : `https://dashboard.stripe.com/${accountKeyContext}`;

    if (isSystemAdmin && accountKey && isRelatedLoaded) {
      if (installmentKey) {
        return `${sysadminBaseUrl}/subscriptions/${installmentKey}`;
      }
      return `${sysadminBaseUrl}/payments/${paymentKey}`;
    } else if (!isSystemAdmin && paymentKey) {
      return `https://dashboard.stripe.com/payments/${paymentKey}`;
    } else if (!isSystemAdmin && installmentKey) {
      return `https://dashboard.stripe.com/subscriptions/${installmentKey}`;
    }

    return null;
  }, [recordData, ticketing.relatedRecord, isSystemAdmin]);

  // Edit action - shown as primary button
  const editAction = useMemo<EntityActionsMenuItem>(() => ({
    key: 'edit-sale',
    label: editLabel,
    icon: <Edit3 aria-hidden="true" />,
    onSelect: handleEditSale
  }), [editLabel, handleEditSale]);

  // Build action menu items in specified order
  const actionMenuItems = useMemo<EntityActionsMenuItem[]>(() => {
    return [
      // Re-issue invoice
      {
        key: CONTEXT_MENU_KEYS.SALES.REISSUE_INVOICE.key,
        label: I18n.t('ticketing.edit.buttons.reissue'),
        icon: <Send aria-hidden="true" />,
        onSelect: handleReissueInvoice
      },
      // Refund or cancel
      {
        key: CONTEXT_MENU_KEYS.SALES.REFUND_OR_CANCEL.key,
        label: I18n.t('ticketing.edit.buttons.refund'),
        icon: <RotateCcw aria-hidden="true" />,
        onSelect: handleRefundOrCancel
      },
      // Separator 1
      { type: 'separator' as const, key: 'separator-1' },
      // View in Stripe
      stripeUrl && {
        key: CONTEXT_MENU_KEYS.SALES.VIEW_IN_STRIPE.key,
        label: I18n.t('donation.donation-view.donation-action.viewInStripe.title'),
        icon: <ExternalLink aria-hidden="true" />,
        href: stripeUrl,
        target: '_blank'
      },
      // Separator 2
      { type: 'separator' as const, key: 'separator-2' },
      // Toggle empty fields
      {
        key: 'toggle-empty-fields',
        label: emptyFieldsLabel,
        icon: showEmptyFields ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />,
        onSelect: handleToggleEmptyFields
      },
      // Toggle currency display
      showCurrencyToggle && currencyToggleLabel && {
        key: 'toggle-currency-display',
        label: currencyToggleLabel,
        icon: <DollarSign aria-hidden="true" />,
        onSelect: handleToggleCurrencyDisplay
      },
      // Audit trail (system admin only)
      isSystemAdmin && saleId && {
        key: CONTEXT_MENU_KEYS.SALES.AUDIT_TRAIL.key,
        label: I18n.t('campaign.performance.header.audit-trail'),
        icon: <History aria-hidden="true" />,
        linkTo: CONTEXT_MENU_KEYS.SALES.AUDIT_TRAIL.getUrl({ id: saleId }, {}),
        systemOnly: true
      }
    ].filter(Boolean) as EntityActionsMenuItem[];
  }, [
    handleReissueInvoice,
    handleRefundOrCancel,
    stripeUrl,
    emptyFieldsLabel,
    showEmptyFields,
    showCurrencyToggle,
    currencyToggleLabel,
    handleToggleEmptyFields,
    handleToggleCurrencyDisplay,
    isSystemAdmin,
    saleId
  ]);

  const isLoading = useMemo(() => {
    return (
      Record.isRecordLoading(ticketing.record) ||
      Record.isRecordLoading(ticketing.manualInstallmentRecord)
    );
  }, [ticketing.record, ticketing.manualInstallmentRecord]);

  if (!recordData) {
    return null;
  }

  return (
    <div className="ui grid sale-detail">
      <BasePageView isLoading={isLoading || isNotStarted}>
        <div className="ticketing-header-container" style={{ alignItems: 'flex-start' }}>
          <TicketingStatus
            ticketData={recordData}
            displayOrgCurrency={displayOrgCurrency}
          />

          <div className="action" style={{ marginLeft: 'auto', flexShrink: 0 }}>
            <EntityHeaderActions
              editAction={editAction}
              menuItems={actionMenuItems}
              actionsLabel={actionsTriggerLabel}
            />
          </div>
        </div>

        <div className="view-detail">
          <div className="view-detail-wrapper">
            <div className="view-detail-main-section">
              <TicketingHeader
                ticketData={recordData}
                displayOrgCurrency={displayOrgCurrency}
              />
              <BillingInformationTable showEmptyFields={showEmptyFields} />
              <TicketInformationTable
                showEmptyFields={showEmptyFields}
                displayOrgCurrency={displayOrgCurrency}
                isSystemAdmin={isSystemAdmin}
              />
              <MerchandiseInformationTable
                showEmptyFields={showEmptyFields}
                displayOrgCurrency={displayOrgCurrency}
                isSystemAdmin={isSystemAdmin}
              />
              <DonationPageInformation donation={recordData} />
            </div>
            <div className="view-detail-side-section ticketing-side-section">
              <TicketPaymentTable
                showEmptyFields={showEmptyFields}
                displayOrgCurrency={displayOrgCurrency}
              />
              <OriginCard
                source={get(recordData, 'source')}
                isShowEmptyFields={showEmptyFields}
              />
              <TicketInstallmentTable
                showEmptyFields={showEmptyFields}
                displayOrgCurrency={displayOrgCurrency}
              />
              {isInstallmentsEnabled && !paymentCompleted && (
                <TicketCollectPayment />
              )}
              <TicketCouponCodes />
            </div>
          </div>
        </div>
        <ReceiptSaleWindowContainer />
        <RefundSaleWindow />
        {paymentCollectionStopped && <ViewPaymentUrlWindow />}
        {isInstallmentsEnabled && !paymentCollectionStopped && (
          <CollectPaymentWindow />
        )}
      </BasePageView>
    </div>
  );
};

export default ViewTicketing;
